import { defineStore } from 'pinia';
import { navigateTo, useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { CT_FOOD, CT_NONFOOD } from '~~/common/config/CheckoutTypes';
import { setErrorNotification } from '~~/common/utils/helper';
import { isApiError } from '~~/common/utils/apiError';
import ObjectCache from '~~/common/utils/ObjectCache';
import { useQueueStore } from '~~/common/stores/queue';
import { useOrderStore } from '~~/common/stores/order';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useDeliveryStore } from '~~/shop/app/stores/delivery';
import { ID_CHECKOUT_TOP, Navigation } from '~~/common/config/Navigation';
import type { AxiosRequestConfig } from 'axios';
import type { DeepPartial, Schema } from '~~/common/types/api';

export type CheckoutData = DeepPartial<Schema<'CheckoutDataModel'>>;
export type Violation = Schema<'CheckoutDataModel.NormalizedConstraintViolationListModel'>;
export type AgreeableStatement = Schema<'CheckoutAgreeableStatementModel'>;
type BillingMethod = Schema<'BillingMethodModel'>;
export type DeliveryMethod = Schema<'DeliveryMethodModel'>;
type PaymentMethod = Schema<'PaymentMethodModel'>;

/** Shop keeps two parallel checkouts, keyed by the lowercased checkout type. */
type ShopCheckoutState = {
  food?: CheckoutData;
  nonfood?: CheckoutData;
};

/**
 * The frontend's own checkout container: on shop the two checkouts side by side, on dealer the
 * single checkout object itself. The shape therefore differs per instance, which is why consumers
 * read the `checkout` getter instead — it resolves to whichever one is currently in play.
 */
type CheckoutState = ShopCheckoutState | CheckoutData;

/** A single available delivery date with the FE-only comparison value attached in `fetchAvailables`. */
type AvailableDate = {
  date: string;
  _comparisonValue?: string;
  [key: string]: unknown;
};
type FrameAvailable = {
  availableDates?: AvailableDate[];
  [key: string]: unknown;
};
type FrameReservation = {
  reservableDate?: string;
  [key: string]: unknown;
};
type FrameState = {
  availables: Record<string, FrameAvailable>;
  reservations: Record<string, FrameReservation>;
  /** Arguments forwarded to `Date.prototype.toLocaleString` for the comparison value. */
  comparisonOptions: [string, Intl.DateTimeFormatOptions];
};

/** A single toaster/notification payload produced by `violationToNoti`. */
type ViolationNotification = {
  type: 'warning';
  manualClose: boolean;
  thinContent: boolean;
  text: {
    subtitle?: string;
    footer: string;
  };
};

type ViolationFilter = RegExp | ((propertyPath?: string) => boolean);

type UpdateShopCheckoutResult = {
  status: boolean;
  error?: unknown;
  isUserExistsError?: boolean;
};

const cache: { updateCheckoutData?: number } = {};
const fetchCheckoutActions = ['order/fetchOrderRequest', 'order/addToCartRequest', 'order/addCouponRequest', 'order/updateCartItemRequest'];
export const loadingIdCheckoutTypeChanged = 'checkout-type-change-loading';

const CT_DEALER = 'DEALER';
const checkoutTypes: Record<string, string> = {
  [CT_DEALER]: CT_DEALER,
};
export const useCheckoutStore = defineStore('checkout', () => {
  const nuxtApp = useNuxtApp();
  const queueStore = useQueueStore();
  const orderStore = useOrderStore();
  const userInterfaceStore = useUserInterfaceStore();
  const deliveryStore = useDeliveryStore();

  /** Every checkout at once. Read it through `checkout`, or `getFoodCheckout` / `getNonfoodCheckout`. */
  const checkouts = ref<CheckoutState>({
    food: {},
    nonfood: {},
  });
  const shopCheckoutType = ref('FOOD');
  const dealerCheckoutType = ref<string>(CT_DEALER);
  const availableAgreeableStatements = ref<AgreeableStatement[]>([]);
  const availableBillingMethods = ref<BillingMethod[]>([]);
  const availablePaymentMethods = ref<PaymentMethod[]>([]);
  const violations = ref<Violation[]>([]);
  const availableDeliveryMethods = ref<DeliveryMethod[]>([]);
  const violationsIsVisible = ref(false);

  const updateCheckoutIsInprogress = ref(false);

  const frame = ref<FrameState>({
    availables: {},
    reservations: {},
    comparisonOptions: ['hu', { timeZone: 'Europe/Budapest', year: '2-digit', month: '2-digit', day: '2-digit' }],
  });

  // getters
  const checkoutType = computed(() => {
    return (__AW_IS_SHOP__ ? shopCheckoutType : dealerCheckoutType).value;
  });

  const getFoodCheckout = computed(() => {
    return asShopCheckouts(checkouts.value).food ?? {};
  });
  const getNonfoodCheckout = computed(() => {
    return asShopCheckouts(checkouts.value).nonfood ?? {};
  });

  /** The checkout currently in play, on either instance. Write through `setCheckout`. */
  const checkout = computed<CheckoutData>(() => {
    if (!__AW_IS_SHOP__) {
      // On dealer the container *is* the checkout.
      return isShopCheckouts(checkouts.value) ? {} : checkouts.value;
    }
    const shopCheckouts = asShopCheckouts(checkouts.value);
    return (shopCheckoutType.value.toLowerCase() === 'nonfood'
      ? shopCheckouts.nonfood
      : shopCheckouts.food
    ) ?? {};
  });
  const getSelection = computed(() => (k: string) => {
    return frame.value.availables[k]?.availableDates?.find(a => a.date === frame.value.reservations[k]?.reservableDate);
  });
  const violationToNoti = computed(() => (filterParam: ViolationFilter): Record<number, ViolationNotification> => {
    return Object.fromEntries(
      violations.value
        .filter((v) => {
          if (typeof filterParam === 'function') {
            return filterParam(v.propertyPath);
          } else { // regex
            return filterParam.test(v.propertyPath);
          }
        })
        .map((v): ViolationNotification => ({
          type: 'warning',
          manualClose: false,
          thinContent: true,
          text: {
            // subtitleToken: v.message,
            subtitle: v.message,
            footer: `ID: ${v.code}`,
          },
        }))
        .map((v, i): [number, ViolationNotification] => [i, v]),
    );
  });

  // actions
  function setCheckoutType (type: string) {
    shopCheckoutType.value = type;
  }

  function setCheckout (payload: { checkout: CheckoutData; type: string } | CheckoutState) {
    if (__AW_IS_SHOP__) {
      setShopCheckout(payload as { checkout: CheckoutData; type: string });
    } else {
      setDealerCheckout(payload as CheckoutState);
    }
  }

  function setShopCheckout ({ checkout: newCheckout, type }: { checkout: CheckoutData; type: string }) {
    checkouts.value = {
      ...checkouts.value,
      [type.toLowerCase()]: newCheckout,
    };
  }

  function setDealerCheckout (newCheckout: CheckoutState) {
    checkouts.value = newCheckout;
  }

  function setAvailableAgreeableStatements (newAvailableAgreeableStatements: AgreeableStatement[]) {
    availableAgreeableStatements.value = newAvailableAgreeableStatements;
  }

  function setAvailableBillingMethods (newAvailableBillingMethods: BillingMethod[]) {
    availableBillingMethods.value = newAvailableBillingMethods;
  }

  function setAvailableDeliveryMethods (newAvailableDeliveryMethods: DeliveryMethod[]) {
    availableDeliveryMethods.value = newAvailableDeliveryMethods;
  }

  function setAvailablePaymentMethods (newAvailablePaymentMethods: PaymentMethod[]) {
    availablePaymentMethods.value = newAvailablePaymentMethods;
  }

  function setViolations (newViolations: Violation[]) {
    violations.value = newViolations;
  }

  function setAvailableFrames (payload: Record<string, FrameAvailable>) {
    frame.value.availables = payload;
  }

  function setReservedFrames (payload: Record<string, FrameReservation>) {
    frame.value.reservations = payload;
  }

  function showViolations () {
    violationsIsVisible.value = true;
  }

  function hideViolations () {
    violationsIsVisible.value = false;
  }

  async function fetchAvailables () {
    const { $api, $logger } = nuxtApp;
    try {
      const data = await $api.$get<Record<string, FrameAvailable>>(`/checkouts/${dealerCheckoutType.value}/available_delivery_dates`);
      for (const a in data) {
        data[a]?.availableDates?.forEach((e) => {
          e._comparisonValue = new Date(e.date).toLocaleString(...frame.value.comparisonOptions);
        });
      }
      setAvailableFrames(data);
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function deleteReservations () {
    const { $api, $logger } = nuxtApp;

    try {
      await $api.$delete(`/checkouts/${dealerCheckoutType.value}/reserved_delivery_dates`);
      setReservedFrames({});
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchReservations () {
    const { $api, $logger } = nuxtApp;

    try {
      setReservedFrames(await $api.$get<Record<string, FrameReservation>>(`/checkouts/${dealerCheckoutType.value}/reserved_delivery_dates`));
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function patchReservations ({ discriminator, reservableDate }: { discriminator: unknown; reservableDate: string }) {
    const { $api, $logger } = nuxtApp;
    try {
      await $api.$patch(
        `checkouts/${dealerCheckoutType.value}/reserved_delivery_dates`,
        {
          discriminator,
          reservableDate,
        },
        {
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
        },
      );
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function deleteCheckout () {
    const { $api, $logger } = nuxtApp;

    try {
      await $api.$delete(`checkouts/${dealerCheckoutType.value}`);
      setDealerCheckout({});
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  function clearCheckoutAvailableDeliveryMethods () {
    setAvailableDeliveryMethods([]);
  }

  /**
   * Shop's entry point to updating a checkout. `updateCheckoutData` has to keep a union return type
   * because it serves both instances, which every shop caller would otherwise have to narrow just to
   * read `status`. Calling this on dealer is a wiring mistake, so it throws rather than returning a
   * shape the caller cannot use.
   */
  async function updateShopCheckout (payload: UpdateShopCheckoutPayload): Promise<UpdateShopCheckoutResult> {
    if (!__AW_IS_SHOP__) {
      throw new Error('updateShopCheckout is shop-only; use updateCheckoutData on dealer.');
    }
    return await runExclusively(() => updateShopCheckoutData(payload));
  }

  async function updateCheckoutData (data: UpdateShopCheckoutPayload | CheckoutData) {
    const { $logger } = nuxtApp;

    return await runExclusively(async () => {
      // The build-time `__AW_IS_SHOP__` flag decides the branch; `isShopCheckoutPayload` narrows the
      // union so neither branch needs an `as` cast.
      if (__AW_IS_SHOP__ && isShopCheckoutPayload(data)) {
        return await updateShopCheckoutData(data);
      }
      if (!__AW_IS_SHOP__ && !isShopCheckoutPayload(data)) {
        return await updateDealerCheckoutData(data);
      }
      $logger.error('updateCheckoutData: payload shape does not match the current instance', { isShop: __AW_IS_SHOP__ });
      return undefined;
    });
  }

  /** Serialises checkout updates: a second call waits until the one in flight has finished. */
  async function runExclusively<T> (action: () => Promise<T>): Promise<T> {
    while (updateCheckoutIsInprogress.value) {
      await new Promise(resolve => setTimeout(resolve, 512));
    }
    updateCheckoutIsInprogress.value = true;
    try {
      return await action();
    } finally {
      updateCheckoutIsInprogress.value = false;
    }
  }

  async function updateShopCheckoutData ({ data, type, isErrorPopupRequested = true, resolveOption }: UpdateShopCheckoutPayload): Promise<UpdateShopCheckoutResult> {
    const { $api, $logger, $localePath } = nuxtApp;
    let dontShowThisErrorPopup;
    try {
      const config: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'application/merge-patch+json',
          ...(resolveOption ? { 'X-Error-Resolve-Option': resolveOption } : {}),
        },
      };

      const r = await $api.patch<CheckoutData>(`checkouts/${type}`, data, config).catch((err: unknown) => {
        if (!isErrorPopupRequested) {
          dontShowThisErrorPopup = err;
        }
        throw err;
      });
      cache.updateCheckoutData = r.status;
      const isCheckoutTypeChanged = r.status === 205;
      if (!isCheckoutTypeChanged) {
        setShopCheckout({ checkout: r.data, type });
      }
      userInterfaceStore.startLoading({ id: loadingIdCheckoutTypeChanged });
      const orderResult = await orderStore.fetchOrder({
        id: 'current',
        fetchNonfoodCheckoutIfNFCartNotEmpty: isCheckoutTypeChanged,
        forceFetchNonfoodCheckout: isCheckoutTypeChanged,
        canStartSecondAction: true,
        canRunSimultaneous: true,
        inDMModal: !isErrorPopupRequested,
      });
      if (orderResult?.status === 428) {
        return {
          status: false,
          error: orderResult,
        };
      }
      if (resolveOption || isCheckoutTypeChanged) {
        await deliveryStore.fetchMethod();
      }
      if (isCheckoutTypeChanged && userInterfaceStore.isCheckoutPage) {
        const newCheckoutType = (type.toUpperCase() === CT_FOOD ? CT_NONFOOD : CT_FOOD).toLowerCase();
        await navigateTo($localePath(`/shop/checkout/${newCheckoutType}#${Navigation[ID_CHECKOUT_TOP]}`));
      }
      return {
        status: !orderResult.fetchOrderErrorStatus,
      };
    } catch (error) {
      const previous = cache.updateCheckoutData;
      const errorObj = isApiError(error) ? error : undefined;
      cache.updateCheckoutData = errorObj?.response?.status;

      const isUserExistsError = Boolean(errorObj?.response?.data?.message?.includes('user_exists'));
      if ((previous === errorObj?.response?.status && previous === 412) || isUserExistsError || (!isErrorPopupRequested && dontShowThisErrorPopup === error)) {
        return {
          status: false,
          error,
          isUserExistsError,
        };
      }

      const { name, message } = await setErrorNotification({ nuxtApp, error, closable: errorObj?.response?.status !== 422 }) || {};
      if (name === 'accept' && errorObj?.response?.status === 422) {
        if (__AW_IS_SHOP__) {
          await navigateTo($localePath('shop-profile-orders'));
        } else {
          await navigateTo($localePath('/orders-list'));
        }
      } else if (name === 'accept' && message) {
        updateShopCheckoutData({ data, resolveOption: message, type, isErrorPopupRequested });
      } else if (name === 'cancel') {
        // do nothing
      } else {
        $logger.error(error);
      }
      return {
        status: false,
        error,
      };
    } finally {
      userInterfaceStore.endLoading({ id: loadingIdCheckoutTypeChanged });
    }
  }

  async function updateDealerCheckoutData (data: CheckoutData) {
    const { $api, $logger } = nuxtApp;

    try {
      hideViolations();
      await $api.$patch<CheckoutState>(
        `checkouts/${dealerCheckoutType.value}`,
        data,
        {
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
        },
      ).then((result) => {
        setDealerCheckout(result);
      });
      await Promise.all([
        $api.$get<Violation[]>(`checkouts/${dealerCheckoutType.value}/violations`).then((newViolations) => {
          setViolations(newViolations);
        }),
        orderStore.fetchOrder({ id: 'current', fetchNonfoodCheckoutIfNFCartNotEmpty: false, fetchFoodCheckout: false }),
      ]);
      return true;
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return false;
    }
  }

  async function fetchCheckout (params?: { type?: string; canRunSimultaneous?: boolean; canStartSecondAction?: boolean }) {
    const { type, canRunSimultaneous, canStartSecondAction } = params || {};

    if (__AW_IS_SHOP__) {
      return await fetchShopCheckout({ type, canRunSimultaneous, canStartSecondAction });
    } else {
      return await fetchDealerCheckout(type ? { type } : { type: CT_DEALER });
    }
  }

  async function fetchDealerCheckout ({ type }: { type?: string } = { type: CT_DEALER }) {
    const { $api, $logger } = nuxtApp;

    const t = type || checkoutType.value;
    if (checkoutTypes[t]) {
      try {
        setDealerCheckout(await $api.$get<CheckoutState>(`checkouts/${t}`));
      } catch (error) {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function fetchShopCheckout ({ type, canRunSimultaneous, canStartSecondAction }: { type?: string; canRunSimultaneous?: boolean; canStartSecondAction?: boolean }) {
    if (import.meta.server) {
      return await fetchCheckoutRequest({ type });
    } else if (queueStore.hasActionsInQueue(fetchCheckoutActions)) {
      if (queueStore.hasFetchCheckoutInQueue('checkout/fetchCheckoutRequest', type)) {
        // return promise with value true to simulate good request
        return new Promise(resolve => resolve(true));
      } else {
        queueStore.add({
          action: fetchCheckoutRequest,
          actionName: 'checkout/fetchCheckoutRequest',
          payload: { type, canRunSimultaneous },
          canRunSimultaneous: false,
          canStartSecondAction,
        });
        return new Promise(resolve => resolve(true));
      }
    } else if (queueStore.hasFetchCheckoutInQueue('checkout/fetchCheckoutRequest', type)) {
      // return promise with value true to simulate good request
      return new Promise(resolve => resolve(true));
    } else {
      return await queueStore.add({
        action: fetchCheckoutRequest,
        actionName: 'checkout/fetchCheckoutRequest',
        payload: { type, canRunSimultaneous },
        canRunSimultaneous: false,
        canStartSecondAction,
      });
    }
  }

  async function fetchCheckoutRequest ({ type, canRunSimultaneous }: { type?: string; canRunSimultaneous?: boolean }) {
    const { $api, $logger, $localePath } = nuxtApp;
    try {
      const data = await $api.$get<CheckoutData>(`checkouts/${type}`);
      setShopCheckout({ checkout: data, type: type || '' });
      return true;
    } catch (error) {
      queueStore.removeAllElementFromQueue(false);

      $logger.error(error);

      // in DB -> Auchan_DeliveryRound.webOrderCloseTime has expired we got 422 HTTP code
      if (isApiError(error) && error.response?.status === 422) {
        const { name } = await setErrorNotification({ nuxtApp, error, closable: false }) || {};
        if (name === 'accept') {
          await orderStore.deleteOrderModification();
          await deliveryStore.fetchMethod();
          await fetchCheckout({ type: checkout.value.type, canRunSimultaneous });
          if (__AW_IS_SHOP__) {
            await navigateTo($localePath('shop-profile-orders'));
          } else {
            await navigateTo($localePath('/orders-list'));
          }
          return false;
        }
      }
      return false;
    }
  }

  async function fetchCheckoutMetaData (type?: string) {
    if (__AW_IS_SHOP__) {
      return await fetchShopCheckoutMetaData(type);
    } else {
      return await fetchDealerCheckoutMetaData(type);
    }
  }

  async function fetchDealerCheckoutMetaData (type?: string) {
    const { $api, $logger } = nuxtApp;
    type = type || dealerCheckoutType.value;
    const [
      newAvailableAgreeableStatements,
      newAvailableBillingMethods,
      newAvailableDeliveryMethods,
    ] = await Promise.all([
      $api.$get<AgreeableStatement[]>(`checkouts/${type}/available_agreeable_statements`).catch(handle),
      $api.$get<BillingMethod[]>(`checkouts/${type}/available_billing_methods`).catch(handle),
      $api.$get<DeliveryMethod[]>(`checkouts/${type}/available_delivery_methods`).catch(handle),
      fetchDealerCheckoutAvailablePaymentMethods(type),
    ]);

    setAvailableAgreeableStatements(newAvailableAgreeableStatements);
    setAvailableBillingMethods(newAvailableBillingMethods);
    setAvailableDeliveryMethods(newAvailableDeliveryMethods);

    function handle (err: unknown) {
      $logger.error(err);
      return [];
    }
  }

  async function fetchShopCheckoutMetaData (type?: string) {
    const { $logger } = nuxtApp;

    try {
      await Promise.all([
        fetchCheckoutViolations(type),
        fetchCheckoutAvailableAgreeableStatements({
          type,
          cacheKey: null,
        }),
        fetchCheckoutAvailableBillingMethods(type),
        fetchCheckoutAvailableDeliveryMethods(type),
        fetchShopCheckoutAvailablePaymentMethods(type),
      ]);
    } catch (error) {
      $logger.error(error);
    }
  }

  async function fetchCheckoutAvailablePaymentMethods (type?: string) {
    if (__AW_IS_SHOP__) {
      return await fetchShopCheckoutAvailablePaymentMethods(type);
    } else {
      return await fetchDealerCheckoutAvailablePaymentMethods(type);
    }
  }

  async function fetchDealerCheckoutAvailablePaymentMethods (type?: string) {
    const { $api, $logger } = nuxtApp;

    type = type || dealerCheckoutType.value;
    try {
      if (!availablePaymentMethods.value?.length) {
        const newAvailablePaymentMethods = await $api.$get<PaymentMethod[]>(`checkouts/${type}/available_payment_methods`);
        setAvailablePaymentMethods(newAvailablePaymentMethods);
      }
    } catch (err) {
      $logger.error(err);
      setAvailablePaymentMethods([]);
    }
  }

  async function fetchShopCheckoutAvailablePaymentMethods (type?: string) {
    const { $api, $logger } = nuxtApp;

    try {
      const newAvailablePaymentMethods = await $api.$get<PaymentMethod[]>(`checkouts/${type}/available_payment_methods`);
      setAvailablePaymentMethods(newAvailablePaymentMethods);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchCheckoutViolations (type?: string) {
    const { $api, $logger } = nuxtApp;

    try {
      const newViolations = await $api.$get<Violation[]>(`checkouts/${type}/violations`);
      setViolations(newViolations);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  const aasCache = new ObjectCache<Promise<AgreeableStatement[]>>('availableAgreeableStatements', '__');

  async function fetchCheckoutAvailableAgreeableStatements ({ type, cacheKey }: { type?: string; cacheKey: string | null }) {
    const { $api, $logger } = nuxtApp;

    try {
      // `ObjectCache` keys on a `string | string[]`. One caller passes `cacheKey: null`, which
      // the old JS forwarded straight into `initCache(null)` → `null.join(...)` → TypeError,
      // so that call *always* fell into the catch below and never cached. Coerce null to '' so
      // the null-key call now works (empty-string cache slot) instead of silently throwing.
      const key = cacheKey ?? '';
      aasCache.initCache(key, () => {
        return $api.$get<AgreeableStatement[]>(`checkouts/${type}/available_agreeable_statements`);
      });
      const newAvailableAgreeableStatements = await aasCache.getCache(key)?.value;
      aasCache.deleteCache(key);
      setAvailableAgreeableStatements(newAvailableAgreeableStatements || []);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchCheckoutAvailableBillingMethods (type?: string) {
    const { $api, $logger } = nuxtApp;

    try {
      const newAvailableBillingMethods = await $api.$get<BillingMethod[]>(`checkouts/${type}/available_billing_methods`);
      setAvailableBillingMethods(newAvailableBillingMethods);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  async function fetchCheckoutAvailableDeliveryMethods (type?: string) {
    const { $api, $logger } = nuxtApp;

    try {
      const newAvailableDeliveryMethods = await $api.$get<DeliveryMethod[]>(`checkouts/${type}/available_delivery_methods`);
      setAvailableDeliveryMethods(newAvailableDeliveryMethods);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
      return [];
    }
  }

  return {
    checkout,
    checkouts,
    shopCheckoutType,
    dealerCheckoutType,
    availableAgreeableStatements,
    availableBillingMethods,
    availablePaymentMethods,
    violations,
    availableDeliveryMethods,
    violationsIsVisible,
    frame,
    checkoutType,
    getFoodCheckout,
    getNonfoodCheckout,
    getSelection,
    violationToNoti,
    setCheckoutType,
    setCheckout,
    setShopCheckout,
    setDealerCheckout,
    setAvailableAgreeableStatements,
    setAvailableBillingMethods,
    setAvailableDeliveryMethods,
    setAvailablePaymentMethods,
    setViolations,
    setAvailableFrames,
    setReservedFrames,
    showViolations,
    hideViolations,
    fetchAvailables,
    deleteReservations,
    fetchReservations,
    patchReservations,
    deleteCheckout,
    clearCheckoutAvailableDeliveryMethods,
    updateCheckoutData,
    updateShopCheckout,
    updateShopCheckoutData,
    updateDealerCheckoutData,
    fetchCheckout,
    fetchDealerCheckout,
    fetchShopCheckout,
    fetchCheckoutRequest,
    fetchCheckoutMetaData,
    fetchDealerCheckoutMetaData,
    fetchShopCheckoutMetaData,
    fetchCheckoutAvailablePaymentMethods,
    fetchDealerCheckoutAvailablePaymentMethods,
    fetchShopCheckoutAvailablePaymentMethods,
    fetchCheckoutViolations,
    fetchCheckoutAvailableAgreeableStatements,
    fetchCheckoutAvailableBillingMethods,
    fetchCheckoutAvailableDeliveryMethods,

  };
});

type UpdateShopCheckoutPayload = {
  data: CheckoutData;
  type: string;
  isErrorPopupRequested?: boolean;
  resolveOption?: string;
};

// The shop container is the only shape carrying `food`/`nonfood` keys; a dealer checkout never has
// them (they are not fields of `CheckoutDataModel`), so their presence discriminates the two.
function isShopCheckouts (state: CheckoutState): state is ShopCheckoutState {
  return 'food' in state || 'nonfood' in state;
}

// Shop-side getters run on the shop instance only, where the container always holds the two keys.
function asShopCheckouts (state: CheckoutState): ShopCheckoutState {
  return isShopCheckouts(state) ? state : {};
}

// The shop payload wraps the checkout data in a `{ data, type }` envelope; a bare CheckoutData
// (dealer) never has a root `data` field (CheckoutDataModel has `type` but no `data`), so `data`
// is the reliable discriminator between the two union members.
function isShopCheckoutPayload (value: UpdateShopCheckoutPayload | CheckoutData): value is UpdateShopCheckoutPayload {
  return typeof value === 'object' && value !== null && 'data' in value;
}
